import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import dayjs from 'dayjs'
import { Card, Grid } from '@mui/material'
import { Button, Modal, Spin, message } from 'antd'

import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import { ContentWrapper } from '../../../../components/ContentWrapper'
import PageUrls from '../../../../constants/page-urls'
import CustomerReportTable from './components/CustomerReportTable'
import CustomerReportFilter, {
  CustomerReportExportType,
  IFilterMapped,
} from './components/CustomerReportFilter'
import ProductPopular from './components/ProductPopular'
import useAxios from '../../../../hooks/useAxios'
import { useUser } from '../../../../hooks/useUser'
import { UserRole } from '../../../../types/user'
import {
  downloadCustomerReportExcel,
  downloadCustomerListExcel,
  fetchAllCustomerReportRecords,
  isExportFetchCanceled,
} from './customerReportExport'

const Menus: any[] = []

const CustomerReportPage = () => {
  const navigate = useNavigate()
  const { user, merchant } = useUser()
  const queryStr = queryString.parse(window.location.search)

  const showProductPopular =
    [UserRole.ADMIN, UserRole.MERCHANT].some((role) =>
      (user.roles as UserRole[])?.includes(role),
    ) || merchant.config?.display?.productPopular !== false

  const init = {
    page: Number(queryStr.page ?? 1),
    perPage: Number(queryStr.perPage ?? 30),
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : dayjs().subtract(30, 'days').format(),
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).startOf('day').format()
      : dayjs().format(),
    minTotal: queryStr.minTotal?.length ? Number(queryStr.minTotal) : undefined,
    maxTotal: queryStr.maxTotal?.length ? Number(queryStr.maxTotal) : undefined,
    productId: queryStr.productId?.length
      ? String(queryStr.productId)
      : undefined,
    categoryId: queryStr.categoryId?.length
      ? String(queryStr.categoryId)
      : undefined,
  }

  const [searchFilter, setSearchFilter] = useState<IFilterMapped>(init)
  const [filter, setFilter] = useState<IFilterMapped>(init)
  const [isExporting, setIsExporting] = useState(false)
  const [exportPage, setExportPage] = useState(0)
  const [exportTotalPages, setExportTotalPages] = useState(0)
  const [exportTotalCustomers, setExportTotalCustomers] = useState<
    number | null
  >(null)
  const [exportPhase, setExportPhase] = useState<'fetch' | 'write'>('fetch')
  const exportAbortRef = useRef<AbortController | null>(null)

  const onFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      setFilter(newFilter)
    },
    [setFilter],
  )

  const handleSearch = (newFilter: IFilterMapped) => {
    setFilter(newFilter)
    setSearchFilter(newFilter)
    const newQuery = queryString.stringify({ ...newFilter })
    navigate(`${PageUrls.Report.Custormer}?${newQuery}`, {
      replace: true,
    })
    setFilter({ ...newFilter })
    setSearchFilter({ ...newFilter })
  }

  // ส่งออก Excel: ใช้ searchFilter (หลังกดค้นหา) ดึงทุกหน้าจนครบ — ไม่ผูกกับเลขหน้าบนตาราง; pageSize ตอนดึงดู CUSTOMER_REPORT_EXPORT_PAGE_SIZE
  const handleExportCancel = useCallback(() => {
    exportAbortRef.current?.abort()
  }, [])

  const handleDownloadExcel = useCallback(
    async (exportType: CustomerReportExportType) => {
      exportAbortRef.current?.abort()
      const ac = new AbortController()
      exportAbortRef.current = ac

      setIsExporting(true)
      setExportPhase('fetch')
      setExportPage(0)
      setExportTotalPages(0)
      setExportTotalCustomers(null)
      try {
        const allRecords = await fetchAllCustomerReportRecords(
          searchFilter,
          ({ page, totalPages, totalCustomers }) => {
            setExportPage(page)
            setExportTotalPages(totalPages)
            if (totalCustomers != null) {
              setExportTotalCustomers(totalCustomers)
            }
          },
          { signal: ac.signal },
        )
        if (!allRecords.length) {
          message.warning('ไม่มีข้อมูลสำหรับส่งออก')
          return
        }
        setExportPhase('write')
        if (exportType === 'customerList') {
          downloadCustomerListExcel(allRecords, searchFilter)
        } else {
          downloadCustomerReportExcel(allRecords, searchFilter)
        }
        message.success('ดาวน์โหลดสำเร็จ')
      } catch (e: any) {
        if (isExportFetchCanceled(e)) {
          message.info('ยกเลิกการส่งออกแล้ว')
          return
        }
        message.error(
          e?.response?.data?.message ??
            e?.message ??
            'ดึงข้อมูลหรือส่งออกไม่สำเร็จ',
        )
      } finally {
        if (exportAbortRef.current === ac) {
          exportAbortRef.current = null
        }
        setIsExporting(false)
      }
    },
    [searchFilter],
  )

  const { data, isLoading, mutate } = useAxios<any>({
    method: 'GET',
    url: '/report/customer-daily-reports',
    params: {
      ...searchFilter,
      startDate: searchFilter.startDate,
      endDate: searchFilter.endDate,
      type: 'order',
      page: searchFilter.page,
      pageSize: searchFilter.perPage,
    },
  })

  // Extract records and pagination info from response
  const records = data?.records || (Array.isArray(data) ? data : [])
  const total = data?.count ?? (Array.isArray(data) ? data.length : 0)
  const hasReportRows = Array.isArray(records) && records.length > 0

  // Fetch product data if productId exists
  interface ProductData {
    _id: string
    code: string
    name: string
  }

  const { data: productData } = useAxios<ProductData>(
    searchFilter.productId
      ? {
          method: 'GET',
          url: `/product/${searchFilter.productId}`,
        }
      : null,
  )

  // Build title with product name if available
  const pageTitle = productData?.name
    ? `รายงานยอดขายลูกค้ารายบุคคล - ${productData.name}`
    : 'รายงานยอดขายลูกค้ารายบุคคล'

  // useEffect(() => {
  //   handleSearch(filter)
  // }, [])

  return (
    <ContentWrapper>
      <Helmet title={pageTitle} />
      <Grid container justifyContent="flex-start" alignItems="center">
        <Grid item>
          <HeaderContent
            goBack
            title={pageTitle}
            routes={Menus}
            descriptions="รายงานยอดขายลูกค้ารายบุคคล"
          />
        </Grid>
      </Grid>

      {showProductPopular && (
        <ProductPopular
          startDate={searchFilter.startDate}
          endDate={searchFilter.endDate}
          limit={15}
          productId={searchFilter.productId}
        />
      )}

      <Card
        sx={{
          padding: 2,
        }}
      >
        <CustomerReportFilter
          filter={filter}
          onFilterChange={onFilterChange}
          onSearch={handleSearch}
          onDownloadExcel={handleDownloadExcel}
          downloadDisabled={isLoading || !hasReportRows}
          isExporting={isExporting}
        />

        <CustomerReportTable
          isLoading={isLoading}
          data={records}
          filter={filter}
          onFilterChange={onFilterChange}
          onSearch={handleSearch}
          total={total}
          mutate={mutate}
        />
      </Card>

      <Modal
        title="ส่งออก Excel"
        open={isExporting}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        width={400}
        destroyOnClose
      >
        <div style={{ textAlign: 'center', padding: '8px 8px 16px' }}>
          <Spin size="large" />
          {exportPhase === 'write' ? (
            <p style={{ marginTop: 20, marginBottom: 0 }}>
              กำลังสร้างไฟล์ Excel...
            </p>
          ) : (
            <>
              <p style={{ marginTop: 20, marginBottom: 8 }}>
                {exportTotalPages > 0
                  ? `กำลังดึงข้อมูลหน้า ${exportPage} / ${exportTotalPages}`
                  : 'กำลังเตรียมข้อมูล...'}
              </p>
              {exportTotalCustomers != null && (
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    color: 'rgba(0,0,0,0.45)',
                  }}
                >
                  รายการลูกค้าทั้งหมด{' '}
                  {exportTotalCustomers.toLocaleString('th-TH')} รายการ
                </p>
              )}
              <Button
                block
                style={{ marginTop: 20 }}
                onClick={handleExportCancel}
              >
                ยกเลิก
              </Button>
            </>
          )}
        </div>
      </Modal>
    </ContentWrapper>
  )
}

export default CustomerReportPage
